<template>
  <v-dialog
    v-model="state.open"
    max-width="420"
    :persistent="state.mode !== 'notify'"
    transition="dialog-transition"
  >
    <v-card class="rounded-xl global-confirm-dialog" :style="cardStyle">
      <!-- Icon with soft background circle -->
      <div class="d-flex justify-center pt-7 pb-1">
        <div class="icon-circle" :style="{ backgroundColor: iconBg }">
          <v-icon :color="iconColor" size="36">{{ iconName }}</v-icon>
        </div>
      </div>

      <!-- Title -->
      <v-card-title
        v-if="state.title"
        :dir="resolvedDirection"
        class="text-center pt-3 pb-1 dialog-title"
      >
        {{ state.title }}
      </v-card-title>

      <!-- Content -->
      <v-card-text
        v-if="state.html || state.text"
        :dir="resolvedDirection"
        class="text-center dialog-text pb-1 pt-1"
      >
        <div v-if="state.html" v-html="sanitizedHtml" />
        <template v-else>{{ state.text }}</template>
      </v-card-text>

      <!-- Checkbox (checkbox mode) -->
      <div v-if="state.mode === 'checkbox'" class="px-8 pt-1">
        <v-checkbox
          v-model="state.checkboxValue"
          :label="state.inputPlaceholder"
          density="compact"
          hide-details
          color="primary"
        />
      </div>

      <!-- Timer progress (notify mode) -->
      <div
        v-if="state.mode === 'notify'"
        class="d-flex justify-center pb-4 pt-3"
      >
        <v-progress-circular
          class="notify-progress"
          :model-value="timerProgress"
          :size="28"
          :width="3"
          color="primary"
        />
      </div>

      <!-- Actions (confirm / checkbox modes) -->
      <v-card-actions
        v-if="state.mode !== 'notify'"
        class="justify-center pb-6 pt-4 ga-3 px-6"
      >
        <v-btn
          v-if="state.showCancelButton"
          variant="outlined"
          rounded="lg"
          class="dialog-btn-cancel"
          min-width="110"
          height="40"
          @click="onCancel"
        >
          {{ state.cancelButtonText }}
        </v-btn>
        <v-btn
          variant="flat"
          rounded="lg"
          class="dialog-btn-confirm"
          min-width="110"
          height="40"
          @click="onConfirm"
          @keydown.enter="onConfirm"
        >
          {{ state.confirmButtonText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import { computed, watch, ref, onBeforeUnmount, type CSSProperties } from 'vue'
import { useConfirmDialog } from '@/composables/useConfirmDialog'

const { state, resolveDialog } = useConfirmDialog()

const rtlTextPattern = /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/

function stripHtmlTags(value: string) {
  return value.replace(/<[^>]*>/g, ' ')
}

function detectDirection(value: string) {
  return rtlTextPattern.test(value) ? 'rtl' : 'ltr'
}

const resolvedDirection = computed(() => {
  if (state.direction === 'rtl' || state.direction === 'ltr') {
    return state.direction
  }

  const content = [state.title, state.text, stripHtmlTags(state.html)]
    .filter(Boolean)
    .join(' ')
    .trim()

  if (!content) {
    return 'ltr'
  }

  return detectDirection(content)
})

const cardStyle = computed<CSSProperties>(() => ({
  direction: resolvedDirection.value as CSSProperties['direction'],
}))

// Icon mapping — softer pastel tones for background circles
const iconMap: Record<string, { icon: string; color: string; bg: string }> = {
  warning: {
    icon: 'mdi-alert-circle-outline',
    color: '#e8a317',
    bg: '#fef5e0',
  },
  success: {
    icon: 'mdi-check-circle-outline',
    color: '#43a047',
    bg: '#e6f4ea',
  },
  error: { icon: 'mdi-close-circle-outline', color: '#e53935', bg: '#fde8e8' },
  question: {
    icon: 'mdi-help-circle-outline',
    color: '#1e88e5',
    bg: '#e3f2fd',
  },
  info: { icon: 'mdi-information-outline', color: '#1e88e5', bg: '#e3f2fd' },
}

const iconName = computed(
  () => iconMap[state.icon]?.icon || 'mdi-information-outline'
)
const iconColor = computed(() => iconMap[state.icon]?.color || '#1e88e5')
const iconBg = computed(() => iconMap[state.icon]?.bg || '#e3f2fd')

// Sanitize HTML to prevent XSS
const sanitizedHtml = computed(() => {
  if (!state.html) return ''
  const div = document.createElement('div')
  div.innerHTML = state.html
  div.querySelectorAll('script').forEach(el => el.remove())
  div.querySelectorAll('*').forEach(el => {
    for (const attr of Array.from(el.attributes)) {
      if (attr.name.startsWith('on')) {
        el.removeAttribute(attr.name)
      }
    }
  })
  return div.innerHTML
})

// Timer logic for notify mode
const timerProgress = ref(100)
let timerFrame: number | null = null
let closeTimeout: ReturnType<typeof setTimeout> | null = null

watch(
  () => state.open,
  isOpen => {
    if (isOpen && state.mode === 'notify') {
      startTimer()
    } else {
      clearTimer()
    }
  }
)

function startTimer() {
  clearTimer()
  const totalMs = Number(state.timer) || 1500
  const endTime = performance.now() + totalMs
  timerProgress.value = 100

  const updateProgress = () => {
    const remainingMs = Math.max(0, endTime - performance.now())
    const progress = (remainingMs / totalMs) * 100
    timerProgress.value = progress

    if (remainingMs > 0) {
      timerFrame = requestAnimationFrame(updateProgress)
    }
  }

  timerFrame = requestAnimationFrame(updateProgress)
  closeTimeout = setTimeout(() => {
    clearTimer()
    timerProgress.value = 0
    resolveDialog(undefined)
  }, totalMs)
}

function clearTimer() {
  if (timerFrame !== null) {
    cancelAnimationFrame(timerFrame)
    timerFrame = null
  }

  if (closeTimeout) {
    clearTimeout(closeTimeout)
    closeTimeout = null
  }
}

onBeforeUnmount(() => {
  clearTimer()
})

function onConfirm() {
  if (state.mode === 'checkbox') {
    resolveDialog({ isConfirmed: true, value: state.checkboxValue })
  } else {
    resolveDialog({ isConfirmed: true })
  }
}

function onCancel() {
  resolveDialog({ isConfirmed: false })
}
</script>

<style scoped lang="scss">
.global-confirm-dialog {
  border: 3px solid transparent;
  background:
    linear-gradient(#fff 0 0) padding-box,
    var(--linearHotPinkDodgerBlue) border-box;
  font-family: Fredoka, sans-serif;
  box-shadow:
    0 12px 40px rgba(74, 114, 255, 0.12),
    0 4px 12px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.icon-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.notify-progress :deep(.v-progress-circular__overlay) {
  transition: none !important;
}

.dialog-title {
  font-family: Fredoka, sans-serif;
  font-weight: 500;
  font-size: 1.15rem;
  line-height: 1.4;
  color: #1f2d37;
  white-space: normal;
  word-break: break-word;
}

.dialog-text {
  font-family: Fredoka, sans-serif;
  font-weight: 400;
  font-size: 0.95rem;
  color: #5a6a76;
  line-height: 1.5;
}

.dialog-btn-confirm {
  background: var(--linearHotPinkDodgerBlue) !important;
  color: white !important;
  font-family: Fredoka, sans-serif;
  font-weight: 500;
  font-size: 0.95rem;
  letter-spacing: 0.01em;
  box-shadow: 0px 4px 0px 0px rgba(47, 52, 203, 0.7);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0px 2px 0px 0px rgba(47, 52, 203, 0.7);
    transform: translateY(2px);
  }

  &:active {
    box-shadow: 0px 0px 0px 0px rgba(47, 52, 203, 0.7);
    transform: translateY(4px);
  }
}

.dialog-btn-cancel {
  border: 2px solid #d0d5dd !important;
  color: #5a6a76 !important;
  font-family: Fredoka, sans-serif;
  font-weight: 500;
  font-size: 0.95rem;
  letter-spacing: 0.01em;
  background: white !important;
  box-shadow: 0px 4px 0px 0px #d0d5dd;
  transition: all 0.2s ease;

  &:hover {
    border-color: #b0b8c1 !important;
    background: #f8f9fa !important;
    box-shadow: 0px 2px 0px 0px #d0d5dd;
    transform: translateY(2px);
  }

  &:active {
    box-shadow: 0px 0px 0px 0px #d0d5dd;
    transform: translateY(4px);
  }
}

// Vuetify dialog transition override — subtle scale + fade
:deep(.v-overlay__content) {
  transition: all 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275) !important;
}
</style>
